import type { PetSheet } from "@highmarch/app";
import type { PetData } from "@highmarch/domain";
import { icon } from "../iconsKit";
import { petPortraitHtml } from "./petArt";
import { roamWords } from "./petWords";

/**
 * The pets on the Towers tab (Pets v2/v3, docs/PETS.md "On screen": "a card per pet: owned ones level like towers,
 * uncaught ones are silhouettes"): a small tile per pet in roster order under the Fortress's card — the portrait in its
 * worn coat, the name, "Lv 7" with an hourglass while it upgrades and the green up-arrow while an upgrade is ready and
 * affordable, a star on the one that comes along; a pet not caught yet is its silhouette and "Roams from level 12".
 * A tap on a caught one opens its sheet (towersTab.ts binds `.pettile[data-pet]`).
 */
export function petTileHtml(s: PetSheet): string {
  const u = s.upgrade, ready = u.state === "ready" && u.affordable, busy = u.state === "running" || u.state === "queued";
  return `<button type="button" class="pettile${busy ? " busy" : ""}${ready ? " ready" : ""}${s.equipped ? " on" : ""}" data-pet="${s.pet.id}" aria-label="${s.pet.name}, level ${s.level}${ready ? ", upgrade ready" : ""}">`
    + `<span class="art">${petPortraitHtml(s.pet.id, 52)}${s.equipped ? `<i class="pt-on" aria-hidden="true">${icon("star", 11)}</i>` : ""}`
    + `<small class="pt-lv">Lv ${s.level}${busy ? ` ${icon("clock", 10)}` : ""}</small></span>`
    + `<b>${s.pet.name}</b>`
    + `${ready ? `<i class="pcd-ready" aria-hidden="true"><svg viewBox="0 0 24 24" width="13" height="13"><path d="M12 4 L20 13 H15 V20 H9 V13 H4 Z" fill="#fff" stroke="#1e5a14" stroke-width="1.6" stroke-linejoin="round"/></svg></i>` : ""}</button>`;
}

/** A pet not caught yet: its silhouette against a starry night, its name, and a small tag with the level it first roams. */
export function petLockedTileHtml(pet: PetData): string {
  const where = roamWords(pet.roamFromLevel);
  return `<div class="pettile uncaught" data-locked="${pet.id}" aria-label="${pet.name}: ${where}"><span class="art"><i class="pt-stars" aria-hidden="true"></i>${petPortraitHtml(pet.id, 52, null)}`
    + `<small class="pt-tag">${icon("lock", 10)}Level ${pet.roamFromLevel}</small></span><b>${pet.name}</b></div>`;
}

/** The row of tiles: every pet in roster order (`sheets` the caught ones' sheets, by pet id). */
export function petTilesHtml(roster: readonly PetData[], sheets: ReadonlyMap<string, PetSheet>): string {
  const tiles = roster.map((p) => { const s = sheets.get(p.id); return s ? petTileHtml(s) : petLockedTileHtml(p); }).join("");
  return tiles ? `<div class="petcards">${tiles}</div>` : "";
}
